import { Component, OnInit, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
    IAOTSCOrg,
    IAOTSCOrgs,
    ITCIApplication,
    ITCIApplicationCopy,
    ITCIApplicationTenancyRequest,
    ITCIApplicationTenancyResponse,
    TCIApplicationTenancyAction,
    TCI_ENVIRONMENT_VAR_TYPE,
    ITCIEnvVars,
    TCITenantSelectorType
} from "@tci/common";
import * as lodash from "lodash";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details/ng-app-details-menu";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";
import { ITCIAppDetailsMenuOutput } from "../../ng-tenant/tci-tenant-components";
import { BaseAppHMCopyService } from "./base-app-hm-copy.service";

import { Observable, Subscription } from "rxjs";
import { FormControl } from "@angular/forms";
import { from } from "rxjs/observable/from";
import { concatMap, toArray } from "rxjs/operators";

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_COPY_APPLICATION.toString(),
    templateUrl: "./base-app-hm-copy.component.html",
    styleUrls: ["./base-app-hm-copy.component.less"]
})
export class BaseAppHMCopyComponent<E, I extends BaseAppDetailMenuComponentInput,
    O extends BaseAppDetailMenuComponentOutput<ITCIAppDetailsMenuOutput>>
    extends AbstractTCITenantComponent<E, I, O>
    implements OnInit, OnDestroy {

    protected subscriptions: Subscription[] = [];
    protected sourceApp: ITCIApplication;
    protected appStatusCodeClass: string;

    protected tscOrgs: IAOTSCOrg[] = [];
    protected tscOrgsOriginal: IAOTSCOrg[] = [];
    protected selectedTscOrg: IAOTSCOrg;
    protected currentOrgName: string;
    protected newAppName: string;

    protected appNamePattern = "^[a-zA-Z_][a-zA-Z0-9-_.]{0,69}$";
    protected nameValid = true;
    protected isCopyingApp = false;
    protected loadingText = "Copying...";
    protected page = 1;
    protected isOrgLoading = true;
    protected nameFilterCtrl = new FormControl();
    // tslint:disable-next-line: member-ordering
    constructor(
        protected copyAppService: BaseAppHMCopyService,
        protected translate: TranslateService,
        protected toastr: NgToastrService) {
        super();
    }

    ngOnInit(): void {
        const inputObj = this.input as BaseAppDetailMenuComponentInput;
        this.sourceApp = inputObj.application;

        const thisObj = this;
        const desiredInstance = inputObj.application.desiredInstanceCount;
        this.subscriptions.push(this.copyAppService.getAppRuningStatus(this.sourceApp.id, desiredInstance).subscribe(
            (rsp) => {
                if (!rsp.success) {
                    thisObj.appStatusCodeClass = "error";
                } else {
                    if (inputObj.application.info && inputObj.application.info.num_instances > 0) {
                        thisObj.appStatusCodeClass = (rsp.failedInstances > 0) ? "error" : "running";

                    } else {
                        thisObj.appStatusCodeClass = "stopped";
                    }
                }
            }, (err) => {
                thisObj.appStatusCodeClass = "error";
            }
        ));

        // GET_TSC_ORGS_CONCURRENT env variable true/false value will change the way to call TSC org list api
        // true: will call tsc api concurrently, call multiple tsc org list api at same time.
        // false: will call serially, call tsc org list api page by page.
        Observable.forkJoin([
            this.copyAppService.getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.GET_TSC_ORGS_CONCURRENT)
                .map((envVars: ITCIEnvVars<boolean>) => envVars[TCI_ENVIRONMENT_VAR_TYPE.GET_TSC_ORGS_CONCURRENT]),
            this.copyAppService.getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.GET_TSC_ORGS_PAGE_SIZE)
                .map((envVars: ITCIEnvVars<number>) => envVars[TCI_ENVIRONMENT_VAR_TYPE.GET_TSC_ORGS_PAGE_SIZE])
        ])
            .subscribe(result => {
                const isConcurrent = result[0];
                const pageSize = result[1];
                this.getAllOrgs(isConcurrent, pageSize);
            });

        this.newAppName = this.sourceApp.name + "_copy";
    }

    ngOnDestroy(): void {
        this.subscriptions
            .filter(s => !s.closed)
            .forEach(s => s.unsubscribe());
    }

    getAllOrgs(isConcurrent: boolean, pageSize: number) {
        // call api to get first page data and the number of records
        this.subscriptions.push(this.copyAppService.getOrganizationsV2(this.page, pageSize).subscribe((res: IAOTSCOrgs) => {
            let resTscOrgs: IAOTSCOrg[] = lodash.clone(res.orgs);
            // first page api call has got all the orgs, no need to call the second page
            if (res.numberOfRecords <= pageSize) {
                this.isOrgLoading = false;
                resTscOrgs = lodash.sortBy(resTscOrgs, "displayName");
                this.tscOrgs = resTscOrgs;
                this.tscOrgsOriginal = lodash.clone(this.tscOrgs);
                this.initFilterOrgList();
            } else {
                // call api with pagination for remaining org list data
                this.subscriptions.push(this.getRemainOrgs(this.page + 1, res.numberOfRecords, isConcurrent, pageSize)
                    .subscribe((resPages: IAOTSCOrgs[]) => {
                        resPages.forEach(resPage => {
                            resTscOrgs = resTscOrgs.concat(resPage.orgs);
                        });
                        resTscOrgs = lodash.sortBy(resTscOrgs, "displayName");
                        this.tscOrgs = resTscOrgs;
                        this.tscOrgsOriginal = lodash.clone(this.tscOrgs);
                        this.initFilterOrgList();
                    }, () => {
                        this.toastr.error("Fail to get organizations for app copy.");
                    }, () => {
                        this.isOrgLoading = false;
                    }));
            }
        }, () => {
            this.isOrgLoading = false;
            this.toastr.error("Fail to get organizations for app copy.");
        }));
    }

    /**
     * get all org list from "page" to the end
     * @param page
     * @param total
     * @param isConcurrent
     * @param pageSize
     */
    getRemainOrgs(page: number, total: number, isConcurrent: boolean, pageSize: number): Observable<IAOTSCOrgs[]> {
        const pages = Math.ceil(total / pageSize);
        const obs: Array<Observable<IAOTSCOrgs>> = [];
        // the first page data has been returned, so i from the second page
        for (let i = page; i <= pages; i++) {
            obs.push(this.copyAppService.getOrganizationsV2(i, pageSize));
        }
        // Note: when isConcurrent is true, will call tsc api concurrently, when it is false, will call serially
        if (isConcurrent) {
            return Observable.combineLatest(obs);
        } else {
            return from(obs)
                .pipe(
                    concatMap(observable => observable),
                    toArray()
                );
        }
    }

    initFilterOrgList() {
        this.nameFilterCtrl.valueChanges
            .debounceTime(300)
            .subscribe(() => {
                this.tscOrgs = lodash.filter(this.tscOrgsOriginal, v => {
                    return lodash.isEmpty(this.nameFilterCtrl.value) ||
                        v.displayName.toLowerCase().trim().indexOf(this.nameFilterCtrl.value.toLowerCase().trim()) >= 0;
                });
            });
    }

    selectTscOrg(index): void {
        this.selectedTscOrg = this.tscOrgs[index];

        const filteredOrgs = lodash.filter(this.tscOrgs, (obj) => {
            return obj.isCurrentOrg;
        });

        if (filteredOrgs && filteredOrgs.length > 0) {
            this.currentOrgName = filteredOrgs[0].displayName;
        }
    }

    copyApp(): void {
        const inputObj = this.input as BaseAppDetailMenuComponentInput;

        const copyData: ITCIApplicationCopy = {
            targetSbsc: this.selectedTscOrg.subscriptionId,
            targetAppName: this.newAppName,
            targetOrgName: this.selectedTscOrg.displayName,
            currentOrgName: this.currentOrgName
        };
        const reqParam: ITCIApplicationTenancyRequest = {
            tenantType: inputObj.tenantAppType,
            tenantAction: TCIApplicationTenancyAction.TENANT_COPY_APPLICATION,
            appId: this.sourceApp.id,
            tenantAppCopy: copyData
        };

        const thisObj = this;
        this.isCopyingApp = true;
        this.copyAppService.copyApp(reqParam)
            .subscribe((rsp: ITCIApplicationTenancyResponse) => {
                thisObj.toastr.clear();
                thisObj.toastr.success("The app " + thisObj.newAppName + " was copied successfully to organization " +
                    thisObj.selectedTscOrg.displayName);
                thisObj.closeModal(true);
                this.isCopyingApp = false;

            }, (err) => {
                thisObj.toastr.clear();
                thisObj.toastr.error(err.msg || err.result.errorMsg);
                this.isCopyingApp = false;
                // thisObj.closeModal();
            });
    }

    newNameCheck(): void {
        if (new RegExp(this.appNamePattern).test(this.newAppName)) {
            this.nameValid = true;
        } else {
            this.nameValid = false;
        }
    }

    cancel(): void {
        this.closeModal(false);
    }

    protected closeModal(refresh: boolean): ITCIAppDetailsMenuOutput {
        const e: ITCIAppDetailsMenuOutput = {
            menuItemSelector: this.input.componentSelector,
            closeModal: true,
            refresh
        };
        this.output.emit(e);
        return e;
    }
}
